import React, { useEffect } from 'react';
import { useListSessionsForLocationQuery } from '../../store/api/localApi';
import { addDays, dublinDateString, dublinTimeHHmm } from '../../utils/date';
import { formatDayMonth } from '../../utils/terms';

export interface EffectiveFromFieldProps {
  /** The date input's `data-testid` (`edit-days-from`, `enroll-from`, `bulk-from`). */
  testId: string;
  classId: string;
  /** The class's location: today's sessions are read per location. */
  locationId: string;
  /** YYYY-MM-DD. Empty until the field has settled on its default. */
  value: string;
  onChange: (date: string) => void;
  /**
   * A floor the dialog knows besides the session rule: the term's start, a
   * pending row's own start (`startsOn`), or the `earliest` the server sent
   * back. The field never offers a date before it.
   */
  notBefore?: string;
  /** The latest allowed date (the term's last day), when the dialog knows it. */
  max?: string;
  /** The start of a change already scheduled on this row (roster `upcoming.from`). */
  upcomingFrom?: string;
  disabled?: boolean;
}

/**
 * The From date of an enrolment change. Its earliest date mirrors the
 * server's rule (`earliestChangeDate`): today, or tomorrow once the class has
 * started today — its session was opened AND its scheduled start time has
 * passed (Dublin time). Opened alone is not enough: registers are opened
 * first thing for every class, and a 15:00 class is not running at 10:40.
 * From then on today belongs to the record. The server enforces it; this
 * just keeps the admin from being refused. A session row without a
 * `scheduledStart` counts as running once opened (the server falls back to
 * the class's start time, so it may allow today where this offers tomorrow).
 *
 * Today's session is found with the read-only location list rather than the
 * class+date lookup, which lazily creates a session row as a side effect.
 */
const EffectiveFromField: React.FC<EffectiveFromFieldProps> = ({
  testId,
  classId,
  locationId,
  value,
  onChange,
  notBefore,
  max,
  upcomingFrom,
  disabled,
}) => {
  const today = dublinDateString();
  const tomorrow = addDays(today, 1);
  // Refetched on every open: starting a session invalidates only that
  // session's own tag, so a cached list could still say "not started".
  const { data: sessionsToday } = useListSessionsForLocationQuery(
    { locationId, date: today },
    { skip: !locationId, refetchOnMountOrArgChange: true }
  );
  const now = dublinTimeHHmm();
  const sessionStarted = !!sessionsToday?.sessions?.some(
    (s) =>
      s.classId === classId && !!s.startedAt && (!s.scheduledStart || now >= s.scheduledStart)
  );
  const sessionEarliest = sessionStarted ? tomorrow : today;
  const earliest = notBefore && notBefore > sessionEarliest ? notBefore : sessionEarliest;
  // The note explains the session rule only when it is what moved the date:
  // a floor later than today (a future term, a pending start) has its own
  // reason and the note would be wrong.
  const forcedBySession = sessionStarted && (!notBefore || notBefore <= today);

  // The default is the earliest date, and a value that has fallen below it
  // (the class started today, the term changed) follows it up.
  useEffect(() => {
    if (!value || value < earliest) onChange(earliest);
  }, [value, earliest, onChange]);

  const replaces = !!upcomingFrom && !!value && value <= upcomingFrom;

  return (
    <div>
      <label htmlFor={testId} className="block text-sm font-medium text-gray-700 mb-1">
        From
      </label>
      <input
        id={testId}
        data-testid={testId}
        type="date"
        value={value}
        min={earliest}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
      />
      {forcedBySession && (
        <p className="mt-1 text-xs text-gray-500" data-testid="effective-from-note">
          Today&apos;s session has already started, so this applies from tomorrow.
        </p>
      )}
      {replaces && upcomingFrom && (
        <p className="mt-1 text-xs text-amber-700" data-testid="effective-from-replace-warning">
          This replaces the change scheduled for {formatDayMonth(upcomingFrom)}.
        </p>
      )}
    </div>
  );
};

/**
 * The admin-facing message for a refused From date, or null when the error is
 * not about the date. `earliest` is set for a too-early refusal: the dialog
 * moves its input there and makes it the minimum. `suggested` is set for a
 * date the child is not enrolled on: the dialog moves its input to the start
 * of their next period.
 */
export function effectiveFromErrorMessage(
  data: any
): { message: string; earliest?: string; suggested?: string } | null {
  const code = data?.code ?? data?.error;
  if (code === 'EFFECTIVE_FROM_TOO_EARLY' && data?.earliest) {
    return {
      message: `Changes can start from ${formatDayMonth(data.earliest)} at the earliest.`,
      earliest: data.earliest,
    };
  }
  if (code === 'EFFECTIVE_FROM_AFTER_TERM' && data?.termEndDate) {
    return { message: `This term ends on ${formatDayMonth(data.termEndDate)}.` };
  }
  if (code === 'EFFECTIVE_FROM_AFTER_END' && data?.endDate) {
    return {
      message: `This enrolment ends on ${formatDayMonth(
        data.endDate
      )}. Enrol the child again to change their days after that.`,
    };
  }
  if (code === 'EFFECTIVE_FROM_IN_GAP' && data?.nextStart) {
    return {
      message: `The child is not enrolled on that date; their next period starts ${formatDayMonth(
        data.nextStart
      )}.`,
      suggested: data.nextStart,
    };
  }
  if (code === 'STALE_ENROLLMENT') {
    return {
      message: data?.message || 'This enrolment was changed by someone else. Reload and try again.',
    };
  }
  return null;
}

export default EffectiveFromField;
